import { FunctionComponent, ReactNode } from 'react';

import { Box, Stack } from '@mui/material';

import { IAppPalette } from './appPalette';

interface IPhoneFrameProps {
  palette: IAppPalette;
  children: ReactNode;
  /**
   * Pulls the whole device back without changing a single colour inside it. Used when two phones are
   * shown together and only one of them is the answer — a greyed-out phone would misrepresent the
   * theme it is supposed to be demonstrating.
   */
  dimmed?: boolean;
  width?: number;
}

const BEZEL = '#1C1A18';

/**
 * A phone, drawn.
 *
 * Every preview of the mobile app in Settings is this frame with something different inside it, so
 * that "what your dancers see" looks like one consistent place rather than a set of unrelated
 * mock-ups. It draws the bezel, the notch and the tab bar — the chrome that makes a rounded
 * rectangle read as a phone at a glance — and nothing else.
 *
 * The tab bar is part of the frame rather than the content because it is the one piece of the app
 * that is on screen no matter which page a dancer is looking at.
 */
export const PhoneFrame: FunctionComponent<IPhoneFrameProps> = (props: IPhoneFrameProps) => {

  const { palette, children, dimmed, width = 208 } = props;

  return (
    <Box
      aria-hidden
      sx={{
        width,
        borderRadius: 4,
        border: `6px solid ${BEZEL}`,
        backgroundColor: palette.page,
        overflow: 'hidden',
        boxShadow: 3,
        mx: 'auto',
        opacity: dimmed ? 0.42 : 1,
        transition: 'opacity 0.25s ease, background-color 0.25s ease'
      }}
    >
      {/* The notch, so it reads as a phone at a glance rather than as a rounded rectangle. */}
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.75 }}>
        <Box sx={{ width: 54, height: 5, borderRadius: 3, backgroundColor: BEZEL }} />
      </Box>

      <Stack sx={{ px: 1.75, pt: 1.5, pb: 2 }} rowGap={1.5}>
        {children}
      </Stack>

      <Box sx={{ display: 'flex', justifyContent: 'space-around', borderTop: `1px solid ${palette.hairline}`, py: 0.75 }}>
        {/* Home is selected, as it is when a dancer opens the app. */}
        {[0, 1, 2, 3].map((index) => (
          <Box
            key={index}
            sx={{
              width: 14,
              height: 4,
              borderRadius: 1,
              backgroundColor: index === 0 ? palette.accent : palette.medium,
              opacity: index === 0 ? 1 : 0.5,
              transition: 'background-color 0.25s ease'
            }}
          />
        ))}
      </Box>
    </Box>
  );
};
